import { createRoot } from 'react-dom/client';
import { ApolloProvider } from '@apollo/client';
import $ from 'jquery';

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { Root } from 'react-dom/client';
import { getValidEmail, isEmailValid } from './utils/validators';
import { ReportEmailRecipientsList } from './components/pages/addressBook/components/ContactAutocomplete/ReportEmailRecipientsList';

const RECIPIENTS_SPLIT_RE = /[\n,;]/;
const TEXTAREA_JOIN_SEPARATOR = '\n';
const DEFAULT_FIELD_ID = 'report-email-recipients';

function withTrailingEmptyValue(values: string[]) {
    if (values.length === 0 || values[values.length - 1] !== '') {
        return [...values, ''];
    }
    return values;
}

function normalizeValues(values: string[]) {
    return withTrailingEmptyValue(values.map((value) => value ?? ''));
}

function getValidValues(values: string[]) {
    return values.filter((value) => value.length > 0);
}

function isValueValid(value: string) {
    return value.length === 0 || isEmailValid(value);
}

function areValuesValid(values: string[]) {
    return values.every((value) => isValueValid(value));
}

export class ReportEmailRecipientsField {
    textarea: JQuery<HTMLTextAreaElement>;

    container: JQuery<HTMLDivElement>;

    root: Root;

    client: ApolloClient<NormalizedCacheObject>;

    values: string[];

    showErrors: boolean;

    isValid: boolean;

    isDisabled: boolean;

    fieldId: string;

    // Last row explicitly focused by the user.
    focusedRowIndex: number | null;

    constructor(selector: string, client: ApolloClient<NormalizedCacheObject>) {
        this.textarea = $(selector);
        this.container = $('<div></div>');
        this.textarea.after(this.container);
        this.root = createRoot(this.container.get(0)!);
        this.client = client;
        this.values = [];
        this.showErrors = false;
        this.isValid = true;
        this.isDisabled = false;
        this.fieldId = String(this.textarea.attr('id') ?? DEFAULT_FIELD_ID);
        this.focusedRowIndex = null;
    }

    create() {
        $('form.form').submit((event) => this.beforeSubmit(event));
        this.textarea.hide();
        this.refresh(false);
    }

    // Keep the existing argument order used by callers.
    // eslint-disable-next-line default-param-last
    refresh(validate = true, disabledOverride?: boolean) {
        const rawValues = String(this.textarea.val() ?? '')
            .split(RECIPIENTS_SPLIT_RE)
            .filter((value) => value.length > 0);
        this.values = normalizeValues(rawValues);
        this.showErrors = validate;
        const textareaDisabled = Boolean(this.textarea.prop('disabled'));
        this.isDisabled = disabledOverride ?? textareaDisabled;
        this.focusedRowIndex = null;
        this.render();
    }

    handleValueChange(index: number, nextValue: string) {
        const isLastIndex = index === this.values.length - 1;
        const previousValue = this.values[index] ?? '';
        const focusedRowValue =
            this.focusedRowIndex !== null ? this.values[this.focusedRowIndex] ?? '' : '';
        const isDifferentFromFocusedRow =
            this.focusedRowIndex !== null && this.focusedRowIndex !== index;
        const isEmptyToNonEmptyTransition = !previousValue && !!nextValue;

        // Chrome can mirror the focused row value into other empty rows that
        // were never focused by the user. Ignore only that clone bleed-through.
        const isAutofillCloneFromFocusedRow =
            isDifferentFromFocusedRow &&
            isEmptyToNonEmptyTransition &&
            focusedRowValue === nextValue;

        if (isAutofillCloneFromFocusedRow) {
            this.showErrors = true;
            this.render();
            return;
        }

        this.values[index] = nextValue;
        this.showErrors = true;
        this.focusedRowIndex = index;

        // Auto-add a new row only when the currently edited row is the last row.
        // This avoids runaway row creation when browser autofill updates multiple rows.
        const shouldAppendTrailingRow = isLastIndex && isEmailValid(nextValue);
        if (shouldAppendTrailingRow) {
            this.values.push('');
        }

        this.render();
    }

    handleRowFocus(index: number) {
        this.focusedRowIndex = index;
    }

    handleAddRow() {
        this.showErrors = true;
        this.focusedRowIndex = null;
        this.values.push('');
        this.render();
    }

    updateTextarea(validValues: string[]) {
        this.textarea.val(
            validValues.map((value) => getValidEmail(value) ?? value).join(TEXTAREA_JOIN_SEPARATOR)
        );
    }

    render() {
        const validValues = getValidValues(this.values);
        const isValid = areValuesValid(this.values);

        this.updateTextarea(validValues);
        this.textarea.parent().toggleClass('-error', this.showErrors && !isValid);
        this.isValid = isValid;

        this.root.render(
            <ApolloProvider client={this.client}>
                <ReportEmailRecipientsList
                    values={this.values}
                    disabled={this.isDisabled}
                    fieldId={this.fieldId}
                    onValueChange={(index, value) => this.handleValueChange(index, value)}
                    onRowFocus={(index) => this.handleRowFocus(index)}
                    onAddRow={() => this.handleAddRow()}
                />
            </ApolloProvider>
        );
    }

    beforeSubmit(event: JQuery.SubmitEvent) {
        this.showErrors = true;
        this.render();

        if (!this.isValid) {
            event.preventDefault();
        }
    }
}
